import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { Button, SpinningLoader } from "risa-oasis-ui_v2";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { StatusLogDataWithId } from "../../../../data-model/statusLogModel";
import { StatusNoteType } from "../../../../../shared/enums/statusNoteType";
import { useCommentsData } from "../../../../hooks/singleMedicalPaOrderDetails/useCommentsData";
import { useCommentsInsert } from "../../../../hooks/singleMedicalPaOrderDetails/useCommentsInsert";
import { BorderCircle } from "../../../../../shared/svg/border-circle";
import { CrossIcon } from "../../../../../shared/svg/cross-icon";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { LogNotes } from "./sidebarComponents/LogNotes";

/** Props accepted by the {@link StatusLog} sidebar component. */
interface StatusLogProps {
  /** Callback invoked when the sidebar is dismissed. */
  onClose: () => void;
  /** PA order ID whose comments are displayed / created. */
  orderId: string;
  /** Optional callback fired when the parent side-modal should also close. */
  onSideModalClose?: () => void;
  /** Current order status, forwarded when persisting a new log entry. */
  status: string;
  /** Hex colour pre-selected for the notes colour picker. */
  preSelectedNotesColor: string;
  /** Patient ID associated with the order. */
  patientId: string;
}

/**
 * Sidebar component for viewing and creating status-log comments on a PA order.
 *
 * Displays two tabs — "Log Event" (create) and "History" (read) — and
 * persists new entries via the order-comments Postgres endpoint.
 */
const StatusLog = (props: StatusLogProps) => {
  const [comments, setComments] = useState<string>("");
  const [selectedFollowUpType, setSelectedFollowUpType] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [notesColor, setNotesColor] = useState<string | null>(
    props?.preSelectedNotesColor ?? null,
  );
  const [activeTab, setActiveTab] = useState<string>("log_event");
  const { data: commentsData, loading: isLoadingComments } = useCommentsData({
    orderId: props.orderId,
    orgId: getOrgIdForFetchExternalWorklist(),
  });
  const {
    runInsert,
    abort,
    loading: isInserting,
  } = useCommentsInsert({
    orderId: props.orderId,
    orgId: getOrgIdForFetchExternalWorklist(),
  });

  useEffect(() => {
    return () => abort();
  }, [abort]);

  // Redux selectors
  const { user } = useAuthSelector((state) => state.firebaseAuthentication);
  const { data: providerDetails } = useAuthSelector(
    (state) => state.providerDetails,
  );
  const [historyData, setHistoryData] = useState<StatusLogDataWithId[]>([]);

  useEffect(() => {
    if (commentsData?.comments && commentsData?.comments.length > 0) {
      const sortedData = commentsData.comments.sort(
        (a, b) =>
          new Date(a.created_at ?? "").getTime() -
          new Date(b.created_at ?? "").getTime(),
      );
      setHistoryData(sortedData);
    }
  }, [commentsData]);

  // Tab elements for switching between screens
  const tabElements = [
    { id: "log_event", label: "Log Event" },
    { id: "history", label: "History" },
  ];

  const handleSaveClick = async () => {
    if (!selectedFollowUpType || !comments.trim()) {
      return;
    }

    if (!user?.email || !providerDetails) {
      return;
    }

    const providerName =
      `${providerDetails.FirstName || ""} ${providerDetails.LastName || ""}`.trim() ||
      "Unknown Provider";

    const result = await runInsert({
      follow_up_type_id: selectedFollowUpType.id,
      follow_up_type_label: selectedFollowUpType.label,
      comments: comments.trim(),
      note_type: props.status as StatusNoteType,
      provider_email: user.email,
      provider_name: providerName,
      notes_color: notesColor ?? "#ffffff",
      patient_id: props.patientId,
    });

    if (result.success) {
      toast.success(ToastMessages.STATUS_LOG_SAVED);
      props.onSideModalClose?.();
      props.onClose();
    } else {
      toast.error(ToastMessages.STATUS_LOG_SAVE_FAILURE);
    }
  };

  return (
    <div className="status-log--main-container flex h-full flex-col">
      <div className="status-log--header mr-auto flex w-full flex-row justify-between border-b-1 border-primaryGray-14 p-4 text-h11 font-bold text-primaryGray-1">
        <div>Status Logs</div>
        <CrossIcon
          onClick={() => {
            props.onClose();
          }}
        />
      </div>

      {/* Tab Navigation */}
      <div className="status-log--tabs flex flex-row border-b border-primaryGray-14 pt-2">
        {tabElements.map((tab) => (
          <div
            key={tab.id}
            className={`status-log--tab cursor-pointer px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-tertiaryBlue-4 bg-tertiaryBlue-13 font-bold text-tertiaryBlue-4"
                : "text-primaryGray-6 hover:border-transparent"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Log Event Screen */}
      <div className="status-log--body flex h-full w-full flex-col gap-4 overflow-y-auto p-4">
        {activeTab === "log_event" && (
          <>
            <LogNotes
              status={props.status}
              setComments={setComments}
              setSelectedFollowUpType={(followUpType) =>
                setSelectedFollowUpType(followUpType)
              }
              setNotesColor={setNotesColor}
              preSelectedNotesColor={notesColor ?? "#ffffff"}
            />
          </>
        )}

        {/* History Screen */}
        {activeTab === "history" && (
          <div className="status-log--history--container flex h-full flex-col gap-2">
            {isLoadingComments ? (
              <div className="flex h-full items-center justify-center py-8">
                <div className="text-center">
                  <SpinningLoader />
                  <p className="mt-2 text-gray-600">Loading history...</p>
                </div>
              </div>
            ) : historyData.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-primaryGray-6">
                No history data available
              </div>
            ) : (
              historyData.map((entry, index) => {
                const createdDate = new Date(entry.created_at ?? "");
                const formattedTime = createdDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                const formattedDate = createdDate.toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "2-digit",
                });

                return (
                  <div key={entry.id} className="flex w-full flex-row gap-2">
                    <div className="flex w-10 flex-col items-center">
                      <BorderCircle />
                      {index < historyData.length - 1 && (
                        <div
                          className="mt-3 flex h-full flex-1 items-start justify-center border-r border-dashed border-primaryGray-14"
                          style={{
                            borderRightStyle: "dashed",
                            borderRightWidth: "1px",
                            borderImage:
                              "repeating-linear-gradient(to bottom, currentColor 0, currentColor 8px, transparent 8px, transparent 16px) 1",
                            borderRightColor: "currentColor",
                          }}
                        ></div>
                      )}
                    </div>
                    <div className="flex w-full flex-col gap-2">
                      <div className="flex flex-row items-start gap-1 text-tiny font-normal text-primaryGray-6">
                        <div>{formattedTime}</div>
                        <div>|</div>
                        <div>{formattedDate}</div>
                      </div>
                      <div>
                        {entry.provider_name} has{" "}
                        <span className="font-semibold">
                          {entry.follow_up_type_label}
                        </span>
                      </div>
                      <div className="status-log--body--follow-up-type--templates bg- flex w-full flex-col gap-2 rounded rounded-s bg-primaryGray-16 p-3">
                        <div className="w-full text-wrap text-primaryGray-6">
                          {entry.comments}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="status-log--footer mt-auto flex w-full flex-row items-end justify-end gap-3 border-t border-primaryGray-15 p-4">
        <Button
          buttonType="secondary"
          size="medium"
          onClick={props.onClose}
          disabled={false}
        >
          {activeTab === "history" ? "Close" : "Cancel"}
        </Button>
        {activeTab === "log_event" && (
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSaveClick}
            disabled={isInserting || !selectedFollowUpType || !comments.trim()}
          >
            {isInserting ? "Saving..." : "Save"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StatusLog;
